import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import useItemsPerPage from "@hooks/useItemsPerPage";
import TableHeaderControls from "../../../components/Project/TableHeaderControls";
import AdvancedSearch from "../../../components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import TableWithPagination from "../../../components/Tables/TableWithPagination";
import { useTheme } from "@hooks/useTheme";
import { CVE_LINK } from "@constants/index";
import { formatDate, formatDateTime } from "../../../utils/common";
import { showErrorToast } from "@configs/react-toastify/snackbar";
import { ComponentHeader } from "@components/Tables/ComponentHeader";
import queryKeys from "@configs/react-query/query-key";
import { useMutationData } from "@hooks/useMutationData";

export default function CVEStatus() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { colors } = useTheme();
    const [currentPage, setCurrentPage] = useState(1);
  const [searchFilters, setSearchFilters] = useState({});
  const [cveList, setCveList] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.CVE_STATUS, DEFAULT_ITEMS_PER_PAGE);
  const [selectedRows, setSelectedRows] = useState([]);
  const isInitialMount = useRef(true);
  const prevCurrentPage = useRef(1);
  const [debouncedSearchFilters, setDebouncedSearchFilters] = useState(searchFilters);
  const debounceTimerRef = useRef(null);
  const [isSearching, setIsSearching] = useState(false);

  const getListData = useMutationData(
    queryKeys.getCVEStatusList,
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setCveList(response.object);
          setTotalData(response.objectCount);
          setTotalPages(Math.ceil(response.objectCount / itemsPerPage));
      }
      },
      onError: (error) => {
      showErrorToast(t("preferences.loading-data-error"));
        console.error("Error during get data:", error);
      },
    },
  );

  const fetchData = useCallback(() => {
    if (getListData.isPending) {
      return;
    }

    const effectiveCriteria = resolveSearchCriteria(searchFilters, {
      quickSearchField: "keyword",
      isAdvancedPanelOpen: showAdvancedSearch,
    });

    const bodyRequest = Object.keys(effectiveCriteria).reduce((acc, key) => {
      const value = effectiveCriteria[key];
      if (value !== "" && value !== undefined) {
        // Format date fields using formatDate function
        if (key === "createdAtFrom" || key === "createdAtTo") {
          acc[key] = formatDate(value);
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {});

    bodyRequest["pageSize"] = itemsPerPage;
    bodyRequest["startRow"] = itemsPerPage * (currentPage - 1);


    getListData.mutate(bodyRequest);
  }, [searchFilters, currentPage, itemsPerPage, getListData.isPending]);

  useEffect(() => {
    if (
      searchFilters &&
      typeof searchFilters === 'object'
    ) {
      fetchData();
      isInitialMount.current = false;
    }
  }, [debouncedSearchFilters, itemsPerPage]);

  useEffect(() => {
    if (!isInitialMount.current && prevCurrentPage.current !== currentPage) {
      fetchData();
      prevCurrentPage.current = currentPage;
    }
  }, [currentPage]);

  // Debounce search input to avoid calling API too frequently
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const hasActualChange = Object.values(searchFilters).some((value) => value && value !== "");
    if (hasActualChange) {
      setIsSearching(true);
    }
    if (!isInitialMount.current) {
      debounceTimerRef.current = setTimeout(() => {
        setDebouncedSearchFilters(searchFilters);
        setIsSearching(false);
      }, 500); // Wait 500ms after user stops typing
    } else {
      setDebouncedSearchFilters(searchFilters);
      setIsSearching(false);
    }
    
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchFilters]);

  const columns = [
    "cve",
    "component",
    "cvssVersion",
    "baseScore",
    "totalActionCnt",
    "notApprovedActionCnt",
    "actionRate",
    "createdAt",
  ];

  const columnConfig = {
    cve: {
      style: {
        whiteSpace: "normal",
        overflowWrap: "initial",
        width: "15%",
      },
      headerRender: () => t("status.cve"),
      customRender: (value, row) => (
        <a
          className={`text-base font-semibold ${colors.textActive}`}
          href={`${CVE_LINK}/${value}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.open(`${CVE_LINK}/${value}`, '_blank', 'noopener,noreferrer');
          }}
        >
          {row?.cve || "-"}
        </a>
      ),
    },
    component: {
      style: {
        whiteSpace: "normal",
        overflowWrap: "initial",
        width: "20%",
      },
      headerRender: () => <ComponentHeader showLicense={false}/>,
      customRender: (value, row) => (
        <div>
          <div className="text-sm">
            {value || "-"} ({row.componentVersion})
          </div>
        </div>
      ),
    },
    cvssVersion: {
      style: {
        whiteSpace: "normal",
        overflowWrap: "initial",
        textAlign: "center",
        width: "10%",
      },
      headerRender: () => t("project.cvss-version"),
    },
    baseScore: {
      style: {
        whiteSpace: "normal",
        overflowWrap: "initial",
        textAlign: "center",
        width: "10%",
      },
      headerRender: () => t("status.score"),
    },
    totalActionCnt: {
      style: {
        whiteSpace: "normal",
        overflowWrap: "initial",
        textAlign: "center",
        width: "10%",
      },
      headerRender: () => t("status.total-action"),
    },
    notApprovedActionCnt: {
      style: {
        whiteSpace: "normal",
        overflowWrap: "initial",
        textAlign: "center",
        width: "10%",
      },
      headerRender: () => t("status.processing-action"),
    },
    actionRate: {
      style: {
        whiteSpace: "normal",
        overflowWrap: "initial",
        textAlign: "center",
        width: "10%",
      },
      headerRender: () => t("status.action-rate"),
      customRender: (value, row) => {
        let actionRate = 0;
        if (row.totalActionCnt > 0) {
          actionRate = Math.ceil((parseInt(row.approvedActionCnt) / parseInt(row.totalActionCnt)) * 100, 2);
        }
        return actionRate + "%";
      },
    },
    createdAt: {
      headerRender: () => t("status.created-date"),
      customRender: (value, row) => formatDateTime(row.createdAt),
    },
  };

  const searchFields = [
    { name: "cve", label: "CVE", placeholder: t("status.cve") },
    { name: "component", label: "Component", placeholder: t("common.component-name") },
    { name: "componentVersion", label: "Component Version", placeholder: t("common.component-version") },
    { name: "cvssVersion", label: "CVSS Version", placeholder: t("project.cvss-version") },
    {
      name: "createdAtFrom",
      label: "Creation Date (From)",
      placeholder: t("common.create-at-from"),
      type: "date",
    },
    {
      name: "createdAtTo",
      label: "Creation Date (To)",
      placeholder: t("common.create-at-to"),
      type: "date",
    },
  ];
  const { showAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: searchFields,
    quickSearchField: "keyword",
    setSearchCriteria: setSearchFilters,
  });

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  const onRowClick = (row) => {
    navigate(`../component-cve-status/${row.id}`);
  };

  const searchHandler = useCallback((inputData) => {
    setSearchFilters(prev => ({
      ...prev,
      ...inputData,
    }));
  }, []);

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid="cve-status-page">
      {/* Header */}
      <div className={`${colors.textPlaceholder} py-1`}>
        <div className="flex items-center justify-between">
          <h1 className={`text-[28px] uppercase font-bold ${colors.textPrimary}`}>{t("status.cve-information-list")}</h1>
        </div>
      </div>

      {/* Controls */}
      <div className={`flex-shrink-0 pt-4 pr-4 ${colors.bgSecondary}`}>
        <TableHeaderControls
          onAdvancedSearchToggle={handleAdvancedSearchToggle}
          onSearch={searchHandler}
          hasSelectedRows={selectedRows.length > 0}
          searchField="keyword"
          searchValue={searchFilters.keyword ?? ""}
          placeholderSearch={t("common.search-placeholder")}
          isDisplayAddButton={false}
          isDisplayDeleteButton={false}
          isSearching={isSearching}
          searchInputTestId="cve-status-search-input"
        />
      </div>

      {/* Advanced Search */}
      {showAdvancedSearch && (
        <div data-testid="cve-status-advanced-search">
          <AdvancedSearch
            title={t("common.advanced-search")}
            searchFields={[...searchFields]}
            searchHandler={searchHandler}
            withoutSearch={true}
          />
        </div>
      )}

      <div className="flex-grow min-h-0" data-testid="cve-status-table">
        <TableWithPagination
          columnConfig={columnConfig}
          data={cveList}
          columns={columns}
          onRowClick={onRowClick}
          loading={getListData.isPending}
          totalPages={totalPages}
          totalData={totalData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          maxRowsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChangeWithReset}
          selectedRows={selectedRows}
          disabledRow={[]}
          shouldShowGoToPage={true}
          showItemsPerPageSelector={true}
        />
      </div>
    </div>
  );
}
